import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';
import {RestaurantSelector} from "@/components/RestaurantSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [formData, setFormData] = useState({
    resturantId: '',
    title: '',
    description: '',
    offerType: 'PERCENTAGE',
    offerValue: 0,
    minOrder: 0,
    maxDiscount: 0,
    startDate: '',
    endDate: '',
    status: true,
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentOffer(null);
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      resturantId: '',
      title: '',
      description: '',
      offerType: 'PERCENTAGE',
      offerValue: 0,
      minOrder: 0,
      maxDiscount: 0,
      startDate: today,
      endDate: today,
      status: true,
    });
    setShowDialog(true);
  };

  const openEditDialog = (offer) => {
    setEditMode(true);
    setCurrentOffer(offer);
    setFormData({
      resturantId: offer.resturantId || '',
      title: offer.title || '',
      description: offer.description || '',
      offerType: offer.offerType || 'PERCENTAGE',
      offerValue: offer.offerValue || 0,
      minOrder: offer.minOrder || 0,
      maxDiscount: offer.maxDiscount || 0,
      startDate: offer.startDate ? new Date(offer.startDate).toISOString().split('T')[0] : '',
      endDate: offer.endDate ? new Date(offer.endDate).toISOString().split('T')[0] : '',
      status: offer.status !== undefined ? offer.status : true,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
      };

      if (editMode && currentOffer) {
        await axios.patch(`${API}/offers/${currentOffer.id}`, submitData);
        toast.success('Offer updated successfully');
      } else {
        await axios.post(`${API}/offers`, submitData);
        toast.success('Offer created successfully');
      }
      
      setShowDialog(false);
      fetchOffers();
    } catch (error) {
      console.error('Failed to save offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    
    try {
      await axios.delete(`${API}/offers/${id}`);
      toast.success('Offer deleted successfully');
      fetchOffers();
    } catch (error) {
      console.error('Failed to delete offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const q = search.trim().toLowerCase();
  const filteredOffers = offers.filter((offer) => {
    if (statusFilter !== 'all' && String(offer.status) !== statusFilter) return false;
    if (!q) return true;
    return (
      (offer.title || '').toLowerCase().includes(q) ||
      (offer.description || '').toLowerCase().includes(q) ||
      (offer.couponCode || '').toLowerCase().includes(q) ||
      (offer.offerType || '').toLowerCase().includes(q) ||
      (offer.restaurant?.name || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            Offers
          </h1>
          <p className="text-gray-600">View and manage restaurant offers and promotions</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button 
              onClick={openCreateDialog}
              data-testid="create-offer-button" 
              className="bg-primary text-white" 
              style={{ backgroundColor: '#C2410C' }}
            >
              <Plus size={20} className="mr-2" />
              Add Offer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="offer-dialog-description">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
              <p id="offer-dialog-description" className="text-sm text-gray-600 mt-1">
                {editMode ? 'Update offer details' : 'Create a new promotional offer'}
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/*<div>*/}
              {/*  <Label htmlFor="resturantId">Restaurant ID *</Label>*/}
              {/*  <Input*/}
              {/*    id="resturantId"*/}
              {/*    data-testid="offer-restaurant-input"*/}
              {/*    value={formData.resturantId}*/}
              {/*    onChange={(e) => setFormData({ ...formData, resturantId: e.target.value })}*/}
              {/*    required*/}
              {/*  />*/}
              {/*</div>*/}

              <RestaurantSelector
                  value={formData.resturantId}
                  onChange={(id) =>
                      setFormData({...formData, resturantId: id})
                  }
                  label="Restaurant (optional)"
                  required={false}
                  allowClear={true}
              />

              <div>
                <Label htmlFor="title">Offer Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., 20% OFF on all orders"
                  data-testid="offer-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="offer-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="offerType">Offer Type *</Label>
                  <Select
                    value={formData.offerType}
                    onValueChange={(value) => setFormData({ ...formData, offerType: value })}
                  >
                    <SelectTrigger data-testid="offer-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                      <SelectItem value="FLAT">Flat Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="offerValue">Offer Value *</Label>
                  <Input
                    id="offerValue"
                    type="number"
                    step="0.01"
                    placeholder={formData.offerType === 'PERCENTAGE' ? '20' : '100'}
                    data-testid="offer-value-input"
                    value={formData.offerValue}
                    onChange={(e) => setFormData({ ...formData, offerValue: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minOrder">Min Order Amount</Label>
                  <Input
                    id="minOrder"
                    type="number"
                    step="0.01"
                    data-testid="offer-min-order-input"
                    value={formData.minOrder}
                    onChange={(e) => setFormData({ ...formData, minOrder: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="maxDiscount">Max Discount</Label>
                  <Input
                    id="maxDiscount"
                    type="number"
                    step="0.01"
                    data-testid="offer-max-discount-input"
                    value={formData.maxDiscount}
                    onChange={(e) => setFormData({ ...formData, maxDiscount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    data-testid="offer-start-date-input"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">End Date *</Label>
                  <Input
                    id="endDate"
                    type="date"
                    data-testid="offer-end-date-input"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status.toString()}
                  onValueChange={(value) => setFormData({ ...formData, status: value === 'true' })}
                >
                  <SelectTrigger data-testid="offer-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                type="submit" 
                data-testid="submit-offer-button" 
                className="w-full" 
                style={{ backgroundColor: '#C2410C' }}
              >
                {editMode ? 'Update Offer' : 'Create Offer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative w-full sm:max-w-sm">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by title, coupon, restaurant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9"
            data-testid="offer-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="offer-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="offers-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Offer
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Restaurant
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Min Order
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Valid Until
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOffers.map((offer) => (
                <tr key={offer.id} className="hover:bg-gray-50 transition-colors" data-testid={`offer-row-${offer.id}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{offer.title}</p>
                      <p className="text-sm text-gray-500">{offer.description || '-'}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-testid={`offer-restaurant-${offer.id}`}>
                    {offer.restaurant ? (
                      <div className="flex items-center gap-3">
                        {offer.restaurant.image ? (
                          <img
                            src={offer.restaurant.image}
                            alt={offer.restaurant.name}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-sm font-semibold border border-orange-200">
                            {offer.restaurant.name?.charAt(0)?.toUpperCase() || 'R'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{offer.restaurant.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {offer.restaurant.phone || offer.restaurant.email || '—'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">All restaurants</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-900">{offer.offerType}</td>
                  <td className="px-6 py-4 text-gray-900">
                    {offer.offerType === 'PERCENTAGE' ? `${offer.offerValue}%` : `₹${offer.offerValue}`}
                  </td>
                  <td className="px-6 py-4 text-gray-900">₹{offer.minOrder || 0}</td>
                  <td className="px-6 py-4 text-gray-900">{formatDate(offer.endDate)}</td>
                  <td className="px-6 py-4">
                    <Badge className={offer.status ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                      {offer.status ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`edit-offer-${offer.id}`}
                        onClick={() => openEditDialog(offer)}
                      >
                        <Edit size={16} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        data-testid={`delete-offer-${offer.id}`}
                        onClick={() => deleteOffer(offer.id)}
                      >
                        <Trash2 size={16} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOffers.length === 0 && (
          <div className="text-center py-12" data-testid="no-offers-message">
            <p className="text-gray-500">
              {offers.length === 0 ? 'No offers found' : 'No offers match your search'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Offers;